import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  VStack,
  useBreakpointValue,
  useColorModeValue,
  Link as ChakraLink,
} from "@chakra-ui/react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import axios from "../axiosConfig";
import { Link } from "react-router-dom";
import TruncatedText from "./common/TruncatedText";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

const ORG_MAX_CHARS = 70;
const SURVEY_MAX_CHARS = 70;

const Dashboard = () => {
  const [stats, setStats] = useState({
    organizations: 0,
    users: 0,
    surveyResponses: 0,
    averageResponseTime: 0,
  });

  const [organizations, setOrganizations] = useState([]);
  const [chartData, setChartData] = useState(null);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  // ✅ Sorting (header style, like other lists)
  const [orgSortKey, setOrgSortKey] = useState("organization"); // organization | responses
  const [orgSortDir, setOrgSortDir] = useState("asc"); // asc | desc

  // Chart dataset colors (ChartJS needs explicit colors)
  const colors = [
    "rgba(255, 99, 132, 1)",
    "rgba(54, 162, 235, 1)",
    "rgba(255, 206, 86, 1)",
    "rgba(75, 192, 192, 1)",
    "rgba(153, 102, 255, 1)",
    "rgba(255, 159, 64, 1)",
    "rgba(99, 255, 132, 1)",
    "rgba(235, 54, 162, 1)",
    "rgba(86, 255, 206, 1)",
    "rgba(64, 159, 255, 1)",
  ];

  const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

  const orgTotalResponses = (org) =>
    (org.surveys ?? []).reduce((sum, s) => sum + (Number(s.respondent_count) || 0), 0);

  const toggleOrgSort = (key) => {
    if (orgSortKey === key) {
      setOrgSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrgSortKey(key);
      setOrgSortDir("asc");
    }
  };

  const OrgSortIndicator = ({ active }) => {
    if (!active) return null;
    return orgSortDir === "asc" ? (
      <TriangleUpIcon ml={2} boxSize="10px" />
    ) : (
      <TriangleDownIcon ml={2} boxSize="10px" />
    );
  };

  const sortedOrganizations = useMemo(() => {
    const dir = orgSortDir === "asc" ? 1 : -1;

    return [...organizations].sort((a, b) => {
      if (orgSortKey === "responses") {
        const diff = orgTotalResponses(a) - orgTotalResponses(b);
        if (diff !== 0) return diff * dir;
        return normalize(a.organization_name).localeCompare(normalize(b.organization_name));
      }

      const cmp = normalize(a.organization_name).localeCompare(normalize(b.organization_name));
      if (cmp !== 0) return cmp * dir;

      // fallback: total responses
      return orgTotalResponses(a) - orgTotalResponses(b);
    });
  }, [organizations, orgSortKey, orgSortDir]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [orgRes, userRes, responseRes, avgTimeRes] = await Promise.all([
          axios.get("/stats/organizations"),
          axios.get("/stats/users"),
          axios.get("/stats/survey_responses"),
          axios.get("/stats/average_response_time"),
        ]);

        setStats({
          organizations: orgRes.data.total,
          users: userRes.data.total,
          surveyResponses: responseRes.data.total,
          averageResponseTime: avgTimeRes.data.average_time,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchOrganizationsWithSurveys = async () => {
      try {
        const response = await axios.get("/stats/organizations_with_surveys");
        const data = response.data ?? [];
        setOrganizations(data);

        const labels = data.map((org) => org.organization_name);
        const datasets = [];

        // Build datasets per survey name
        data.forEach((org) => {
          (org.surveys ?? []).forEach((survey) => {
            const existingDataset = datasets.find((ds) => ds.label === survey.survey_name);
            if (!existingDataset) {
              const colorIndex = datasets.length % colors.length;
              const color = colors[colorIndex];

              datasets.push({
                label: survey.survey_name,
                data: new Array(labels.length).fill(0),
                borderColor: color,
                backgroundColor: color.replace("1)", "0.2)"),
                tension: 0.3,
                fill: true,
              });
            }
          });
        });

        // Populate counts per org index
        data.forEach((org, orgIndex) => {
          (org.surveys ?? []).forEach((survey) => {
            const dataset = datasets.find((ds) => ds.label === survey.survey_name);
            if (dataset) dataset.data[orgIndex] = survey.respondent_count;
          });
        });

        setChartData({ labels, datasets });
      } catch (error) {
        console.error("Error fetching organizations:", error);
      } finally {
        setLoadingOrgs(false);
      }
    };

    fetchOrganizationsWithSurveys();
  }, []);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          title: { display: true, text: "Responses" },
        },
        x: {
          title: { display: false, text: "" },
          ticks: {
            // Prevent ultra-long org names from killing the layout
            callback: function (value) {
              const label = this.getLabelForValue(value);
              if (!label) return "";
              return label.length > 18 ? `${label.slice(0, 18)}…` : label;
            },
          },
        },
      },
      plugins: {
        legend: { display: true, position: "top", labels: { boxWidth: 10 } },
        tooltip: {
          callbacks: {
            footer: (tooltipItems) => {
              const orgIndex = tooltipItems[0].dataIndex;
              const total = tooltipItems.reduce((sum, item) => sum + (Number(item.raw) || 0), 0);
              const orgLabel = chartData?.labels?.[orgIndex] || "Org";
              return `Total Responses for ${orgLabel}: ${total}`;
            },
          },
        },
      },
    }),
    [chartData]
  );

  const formatMinutes = (seconds) => {
    const mins = Number(seconds ?? 0) / 60;
    if (!Number.isFinite(mins)) return "0.00";
    return mins.toFixed(2);
  };

  const StatCard = ({ label, value, suffix }) => (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      p={4}
      _hover={{ shadow: "md" }}
    >
      <Stat>
        <StatLabel color={subTextColor}>{label}</StatLabel>
        <StatNumber fontSize={{ base: "3xl", md: "4xl" }}>
          {value}
          {suffix ? (
            <Text as="span" fontSize="lg" color={subTextColor} ml={2}>
              {suffix}
            </Text>
          ) : null}
        </StatNumber>
      </Stat>
    </Box>
  );

  const OrgSurveyMobileCard = (org) => (
    <Box
      key={org.id}
      p={4}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      bg={cardBg}
      _hover={{ shadow: "md" }}
    >
      <VStack align="start" spacing={3} w="100%">
        <HStack justify="space-between" w="100%" align="start">
          <Box minW={0}>
            <ChakraLink as={Link} to={`/app/organizations/${org.id}`} color="teal.500" fontWeight="bold">
              <TruncatedText value={org.organization_name} maxChars={ORG_MAX_CHARS} />
            </ChakraLink>
            <Text fontSize="sm" color={subTextColor}>
              Total: {orgTotalResponses(org)} • Surveys: {org.surveys?.length ?? 0}
            </Text>
          </Box>

          <Badge variant="subtle" colorScheme="blue" borderRadius="md">
            Org
          </Badge>
        </HStack>

        <VStack align="start" spacing={2} w="100%">
          {(org.surveys ?? []).map((survey) => (
            <Box key={`${org.id}-${survey.survey_name}`} w="100%" minW={0}>
              <HStack justify="space-between" w="100%" align="start">
                <Box minW={0}>
                  <TruncatedText value={survey.survey_name} maxChars={SURVEY_MAX_CHARS} fontWeight="semibold" />
                </Box>
                <Badge variant="subtle" colorScheme="green" borderRadius="md">
                  {survey.respondent_count}
                </Badge>
              </HStack>
            </Box>
          ))}
        </VStack>
      </VStack>
    </Box>
  );

  const OrgSurveyTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
    >
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "48%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>

          <Thead>
            <Tr sx={{ position: "sticky", top: 0, zIndex: 1, background: cardBg }}>
              <Th
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleOrgSort("organization")}
              >
                School <OrgSortIndicator active={orgSortKey === "organization"} />
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Survey
              </Th>

              <Th
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
                isNumeric
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleOrgSort("responses")}
              >
                Responses <OrgSortIndicator active={orgSortKey === "responses"} />
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {sortedOrganizations.map((org) =>
              (org.surveys ?? []).map((survey, idx) => (
                <Tr key={`${org.organization_name}-${survey.survey_name}`}>
                  {idx === 0 && (
                    <Td rowSpan={org.surveys.length} verticalAlign="top" py={3}>
                      <Box minW={0}>
                        <ChakraLink as={Link} to={`/app/organizations/${org.id}`} color="teal.500" fontWeight="bold">
                          <TruncatedText value={org.organization_name} maxChars={ORG_MAX_CHARS} />
                        </ChakraLink>
                        <Text fontSize="sm" color={subTextColor} mt={1}>
                          Total: {orgTotalResponses(org)} • Surveys: {org.surveys?.length ?? 0}
                        </Text>
                      </Box>
                    </Td>
                  )}

                  <Td py={3}>
                    <Box minW={0} w="100%">
                      <TruncatedText value={survey.survey_name} maxChars={SURVEY_MAX_CHARS} />
                    </Box>
                  </Td>

                  <Td py={3} isNumeric fontVariantNumeric="tabular-nums">
                    {survey.respondent_count}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box p={6} w="100%" maxW="100%">
      <Heading mb={6}>Dashboard</Heading>

      {/* Summary Metrics */}
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
        <StatCard label="Total Schools" value={loadingStats ? "—" : stats.organizations} />
        <StatCard label="Total Users" value={loadingStats ? "—" : stats.users} />
        <StatCard label="Respondents" value={loadingStats ? "—" : stats.surveyResponses} />
        <StatCard label="Average Response Time" value={loadingStats ? "—" : formatMinutes(stats.averageResponseTime)} suffix="min" />
      </SimpleGrid>

      <Divider />

      {/* Chart */}
      <Box
        mt={6}
        mb={6}
        border="1px solid"
        borderColor={borderColor}
        bg={cardBg}
        p={4}
        shadow="sm"
        borderRadius="xl"
        height={{ base: "320px", md: "520px" }}
        overflow="hidden"
      >
        <HStack justify="space-between" mb={4} flexWrap="wrap">
          <Heading size="md">Survey Responses Overview</Heading>
          <Text fontSize="sm" color={subTextColor}>
            by school and survey
          </Text>
        </HStack>

        {chartData ? (
          <Box height="100%" width="100%">
            <Line data={chartData} options={chartOptions} />
          </Box>
        ) : (
          <Flex align="center" justify="center" height="100%">
            <HStack spacing={3}>
              <Spinner />
              <Text color={subTextColor}>Loading chart…</Text>
            </HStack>
          </Flex>
        )}
      </Box>

      <Divider />

      {/* Organizations and Surveys Section */}
      <Box mt={6}>
        <HStack justify="space-between" mb={4} flexWrap="wrap">
          <Heading size="md">Schools and Their Surveys</Heading>
          <Text fontSize="sm" color={subTextColor}>
            response counts per survey (click headers to sort)
          </Text>
        </HStack>

        {loadingOrgs ? (
          <Flex justify="center" mt={6}>
            <Spinner size="lg" />
          </Flex>
        ) : sortedOrganizations.length === 0 ? (
          <Text mt={4} textAlign="center">
            No schools or surveys available.
          </Text>
        ) : isMobile ? (
          <VStack spacing={4} align="stretch">
            {sortedOrganizations.map(OrgSurveyMobileCard)}
          </VStack>
        ) : (
          <OrgSurveyTable />
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
